/*
 * Copyright (c) 2022. American Express Company
 */

import React from 'react';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import LiteralsService from 'src/services/literals/LiteralsService';
import FormatService from 'src/services/format.service';

import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Card from 'src/components/atomic/atoms/card/Card';
import { NewTransactionsRecapViewModel } from 'src/types/view-models/new-transactions-recap.view.model';

import styles from './newTransactionsRecap.module.scss';
import colors from 'src/styles/colors.module.scss';
import { LocalDate } from '@js-joda/core';
import { CardVariant } from 'src/types/card-variant.enum';
import Selectors from 'src/utils/selectors';

/**
 * @param topDeposit - top deposit in the last 30 days.
 * @param topExpense - top expense in the last 30 days.
 * @param newTransactions - number of new transactions in the last 30 days.
 * @param handleOpenIncomeSummary - opens the Incomes Drawer with the given date.
 * @param handleOpenExpenses - opens the Expenses Drawer with the given date.
 * @param handleOpenTransactions - opens the Transactions Drawer with the given date.
 */

export default function NewTransactionsRecap({
    topDeposit,
    topExpense,
    newTransactions,
    handleOpenIncomeSummary,
    handleOpenExpenses,
    handleOpenTransactions,
    handleOpenTransactionWithId
}: NewTransactionsRecapViewModel): JSX.Element {
    /** If there are no top deposits in the last 30 days, we show the top deposits of the last 12 months */
    const topDepositStartDate: LocalDate = topDeposit ? LocalDate.now().minusDays(30) : LocalDate.now().minusMonths(12);
    /** If there are no top expenses in the last 30 days, we show the top deposits of the last 12 months */
    const topExpenseStartDate: LocalDate = topExpense ? LocalDate.now().minusDays(30) : LocalDate.now().minusMonths(12);
    /** If there are no new transactions in the last 30 days, we show the top deposits of the last 12 months */
    const newTransactionsStartDate: LocalDate =
        Math.abs(newTransactions) !== 0 ? LocalDate.now().minusDays(30) : LocalDate.now().minusMonths(12);
    const endDate: LocalDate = LocalDate.now();

    /** Depending on whether we have a top deposit or not in the last 30 days, we apply specific styles and
     *  appropriate links */
    const topDepositCard: JSX.Element = topDeposit ? (
        <Card
            variant={CardVariant.SECONDARY}
            className={styles.transactionsRecapCard}
            data-cy={Selectors.PlanAhead.TOP_DEPOSIT_CARD}
        >
            <Typography component="h3" variant={TypographyVariants.BODY_BOLD}>
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.topDeposit')}
            </Typography>
            <Anchor
                onClick={() => {
                    void handleOpenTransactionWithId(topDepositStartDate, endDate, topDeposit.id);
                }}
                data-cy={Selectors.PlanAhead.TOP_DEPOSIT_LINK}
                typographyProps={{ variant: TypographyVariants.HEADING_S }}
            >
                {FormatService.format(Math.abs(topDeposit.amount), false)}
            </Anchor>
            <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                {topDeposit.displayName}
            </Typography>
        </Card>
    ) : (
        <Card
            variant={CardVariant.SECONDARY}
            className={styles.transactionsRecapCard}
            data-cy={Selectors.PlanAhead.TOP_DEPOSIT_CARD}
        >
            <Typography component="h3" variant={TypographyVariants.BODY_BOLD}>
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.topDeposit')}
            </Typography>
            <Typography component="p" variant={TypographyVariants.SUBTEXT} className={colors.textWeak}>
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.noDeposits')}
            </Typography>
            <Anchor
                onClick={() => {
                    void handleOpenIncomeSummary(topDepositStartDate, endDate);
                }}
                data-cy={Selectors.PlanAhead.TOP_DEPOSIT_LINK}
                typographyProps={{ component: 'p', variant: TypographyVariants.SUBTEXT }}
            >
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.viewIncome')}
            </Anchor>
        </Card>
    );

    /** Depending on whether we have a top expense or not in the last 30 days, we apply specific styles and appropriate
     *  links */
    const topExpenseCard: JSX.Element = topExpense ? (
        <Card
            variant={CardVariant.SECONDARY}
            className={styles.transactionsRecapCard}
            data-cy={Selectors.PlanAhead.TOP_EXPENSE_CARD}
        >
            <Typography component="h3" variant={TypographyVariants.BODY_BOLD}>
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.topExpense')}
            </Typography>
            <Anchor
                onClick={() => {
                    void handleOpenTransactionWithId(topExpenseStartDate, endDate, topExpense.id);
                }}
                data-cy={Selectors.PlanAhead.TOP_EXPENSE_LINK}
                typographyProps={{ variant: TypographyVariants.HEADING_S }}
            >
                {FormatService.format(Math.abs(topExpense.amount), false)}
            </Anchor>
            <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                {topExpense.displayName}
            </Typography>
        </Card>
    ) : (
        <Card
            variant={CardVariant.SECONDARY}
            className={styles.transactionsRecapCard}
            data-cy={Selectors.PlanAhead.TOP_EXPENSE_CARD}
        >
            <Typography component="h3" variant={TypographyVariants.BODY_BOLD}>
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.topExpense')}
            </Typography>
            <Typography component="p" variant={TypographyVariants.SUBTEXT} className={colors.textWeak}>
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.noExpenses')}
            </Typography>
            <Anchor
                onClick={() => {
                    void handleOpenExpenses(topExpenseStartDate, endDate);
                }}
                data-cy={Selectors.PlanAhead.TOP_EXPENSE_LINK}
                typographyProps={{ component: 'p', variant: TypographyVariants.SUBTEXT }}
            >
                {LiteralsService.getLiteral('Insights.newTransactionsRecap.viewExpenses')}
            </Anchor>
        </Card>
    );

    /** Depending on whether we have new transactions or not in the last 30 days, we apply specific styles and
     *  appropriate links */
    const newTransactionsCard: JSX.Element =
        Math.abs(newTransactions) !== 0 ? (
            <Card
                variant={CardVariant.SECONDARY}
                className={styles.transactionsRecapCard}
                data-cy={Selectors.PlanAhead.NEW_TRANSACTIONS_CARD}
            >
                <Typography component="h3" variant={TypographyVariants.BODY_BOLD}>
                    {LiteralsService.getLiteral('Insights.newTransactionsRecap.newTransactions')}
                </Typography>
                <Anchor
                    onClick={() => {
                        void handleOpenTransactions(null, newTransactionsStartDate, endDate, '');
                    }}
                    data-cy={Selectors.PlanAhead.NEW_TRANSACTIONS_LINK}
                    typographyProps={{ variant: TypographyVariants.HEADING_S }}
                >
                    {FormatService.formatRounded(newTransactions)}
                </Anchor>
                <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getLiteral('Insights.newTransactionsRecap.categorized')}
                </Typography>
            </Card>
        ) : (
            <Card
                variant={CardVariant.SECONDARY}
                className={styles.transactionsRecapCard}
                data-cy={Selectors.PlanAhead.NEW_TRANSACTIONS_CARD}
            >
                <Typography component="h3" variant={TypographyVariants.BODY_BOLD}>
                    {LiteralsService.getLiteral('Insights.newTransactionsRecap.newTransactions')}
                </Typography>
                <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                    0
                </Typography>
                <Anchor
                    onClick={() => {
                        void handleOpenTransactions(null, newTransactionsStartDate, endDate, '');
                    }}
                    data-cy={Selectors.PlanAhead.NEW_TRANSACTIONS_LINK}
                    typographyProps={{ component: 'p', variant: TypographyVariants.SUBTEXT }}
                >
                    {LiteralsService.getLiteral('Insights.newTransactionsRecap.viewTransactions')}
                </Anchor>
            </Card>
        );

    return (
        <div className={styles.newTransactionsRecap}>
            {topDepositCard}
            {topExpenseCard}
            {newTransactionsCard}
        </div>
    );
}
